import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { optimizeImageUrlFillRectangle, optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer/lib/generateProductLinkPropsForLoggedOutRetailer";
var DOUBLE_IMAGE_RATIO = 3 / 4;
var SINGLE_IMAGE_RATIO = 3 / 2;
var TRIPLE_IMAGE_RATIO = 1;
var IMAGE_HEIGHT = 241;
var getAltTag = function(productName, brandName, strictLocalize) {
    return strictLocalize({
        id: "I7VwPe",
        defaultMessage: [
            {
                type: 0,
                value: "Sign up to buy wholesale "
            },
            {
                type: 1,
                value: "ProductName"
            },
            {
                type: 0,
                value: " by "
            },
            {
                type: 1,
                value: "brandCompanyName"
            },
            {
                type: 0,
                value: "."
            }
        ]
    }, {
        ProductName: productName,
        brandCompanyName: brandName
    });
};
export var BrandReviewTileImages = function(param) {
    var brandToken = param.brandToken, products = param.products, onProductClick = param.onProductClick, brandName = param.brandName;
    var location = useLocation();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isSSR = useIsSSR();
    if (!products) {
        return null;
    }
    switch(products.length){
        case 1:
        case 2:
            return /*#__PURE__*/ _jsx(SingleAndDoubleImageGrid, {
                children: products.map(function(product, index) {
                    var productLinkProps = generateProductLinkPropsForLoggedOutRetailer({
                        productToken: product.product_token,
                        brandToken: brandToken,
                        location: location
                    });
                    var _productLinkProps_productUrl, _product_product_name;
                    return /*#__PURE__*/ _jsx(SingleDoubleImageWrapper, {
                        $length: products.length,
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: (_productLinkProps_productUrl = productLinkProps === null || productLinkProps === void 0 ? void 0 : productLinkProps.productUrl) !== null && _productLinkProps_productUrl !== void 0 ? _productLinkProps_productUrl : "",
                            replace: productLinkProps === null || productLinkProps === void 0 ? void 0 : productLinkProps.replace,
                            onClick: function() {
                                var _product_product_token;
                                return onProductClick((_product_product_token = product.product_token) !== null && _product_product_token !== void 0 ? _product_product_token : "");
                            },
                            children: /*#__PURE__*/ _jsxs(FullSizeAspectPicture, {
                                $ratio: products.length === 1 ? SINGLE_IMAGE_RATIO : DOUBLE_IMAGE_RATIO,
                                $width: "100%",
                                $height: "100%",
                                $hideBorder: true,
                                children: [
                                    /*#__PURE__*/ _jsx(FullSizeImage, {
                                        alt: getAltTag((_product_product_name = product.product_name) !== null && _product_product_name !== void 0 ? _product_product_name : "", brandName !== null && brandName !== void 0 ? brandName : "", strictLocalize),
                                        src: product.image ? optimizeImageUrlFillRectangle(product.image, isSSR, products.length === 1 ? IMAGE_HEIGHT * SINGLE_IMAGE_RATIO : IMAGE_HEIGHT * DOUBLE_IMAGE_RATIO, IMAGE_HEIGHT) : EAEAEA_PIXEL
                                    }),
                                    /*#__PURE__*/ _jsx(TransparencyOverlay, {})
                                ]
                            })
                        })
                    }, "".concat(product.product_token, "-").concat(index));
                })
            });
        default:
            return /*#__PURE__*/ _jsx(TripleImageGrid, {
                children: products.slice(0, 3).map(function(product, index) {
                    var productLinkProps = generateProductLinkPropsForLoggedOutRetailer({
                        productToken: product.product_token,
                        brandToken: brandToken,
                        location: location
                    });
                    var _productLinkProps_productUrl, _product_product_name;
                    return /*#__PURE__*/ _jsx(TripleImageWrapper, {
                        "data-test-id": "image-wrapper-".concat(index),
                        $index: index,
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: (_productLinkProps_productUrl = productLinkProps === null || productLinkProps === void 0 ? void 0 : productLinkProps.productUrl) !== null && _productLinkProps_productUrl !== void 0 ? _productLinkProps_productUrl : "",
                            replace: productLinkProps === null || productLinkProps === void 0 ? void 0 : productLinkProps.replace,
                            onClick: function() {
                                var _product_product_token;
                                return onProductClick((_product_product_token = product.product_token) !== null && _product_product_token !== void 0 ? _product_product_token : "");
                            },
                            children: /*#__PURE__*/ _jsxs(FullSizeAspectPicture, {
                                $ratio: TRIPLE_IMAGE_RATIO,
                                $height: "100%",
                                $hideBorder: true,
                                children: [
                                    /*#__PURE__*/ _jsx(FullSizeImage, {
                                        alt: getAltTag((_product_product_name = product.product_name) !== null && _product_product_name !== void 0 ? _product_product_name : "", brandName !== null && brandName !== void 0 ? brandName : "", strictLocalize),
                                        src: product.image ? optimizeImageUrlFillSquare(product.image, isSSR, IMAGE_HEIGHT) : EAEAEA_PIXEL
                                    }),
                                    /*#__PURE__*/ _jsx(TransparencyOverlay, {})
                                ]
                            })
                        })
                    }, "".concat(product.product_token, "-").concat(index));
                })
            });
    }
};
var TripleImageGrid = styled.div.withConfig({
    componentId: "sc-ab4d78df-0"
})([
    "display:grid;grid-template-columns:2fr 1fr;grid-template-rows:1fr 1fr;grid-gap:2px;"
]);
var TripleImageWrapper = styled.div.withConfig({
    componentId: "sc-ab4d78df-1"
})([
    "",
    ""
], function(param) {
    var $index = param.$index;
    return $index === 0 ? "grid-row: 1 / span 2;" : "";
});
var SingleAndDoubleImageGrid = styled.div.withConfig({
    componentId: "sc-ab4d78df-2"
})([
    "height:100%;display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr;grid-gap:2px;"
]);
var SingleDoubleImageWrapper = styled.div.withConfig({
    componentId: "sc-ab4d78df-3"
})([
    "",
    ""
], function(param) {
    var $length = param.$length;
    return $length === 1 ? "grid-column: 1 / span 2;" : "";
});
var FullSizeAspectPicture = styled(AspectRatioPicture).withConfig({
    componentId: "sc-ab4d78df-4"
})([
    "width:100%;height:100%;"
]);
var FullSizeImage = styled.img.withConfig({
    componentId: "sc-ab4d78df-5"
})([
    "width:100%;height:100%;"
]);
